import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider } from "./auth/AuthProvider.jsx";
import { RequireAuth } from "./auth/RequireAuth.jsx";
import { Layout } from "./components/Layout.jsx";
import { ToasterProvider } from "./components/Toaster.jsx";
// Home.jsx is the LEGACY home — superseded by Today.jsx (the editorial
// per-user customizable dashboard). The "/" route now redirects to /today
// so no one lands on the old page anymore. Home.jsx kept on disk for
// reference until we delete it in a follow-up pass.
import { Inventory } from "./pages/Inventory.jsx";
import { CheckIn } from "./pages/CheckIn.jsx";
import { Manager } from "./pages/Manager.jsx";
import { AddUnit } from "./pages/AddUnit.jsx";
import { ImportDar } from "./pages/Import.jsx";
import { TvMode } from "./pages/TvMode.jsx";
import { Report } from "./pages/Report.jsx";
// Settings page lives on the portal now (portal.sevric.com/store-settings.html).
// Store-level config (logo, ZIP, cycle thresholds) is platform config that
// every SEVRIC app reads from /stores/{storeId} — apps don't grow their own
// settings UIs. See SEVRIC_HANDOFF.md "Architectural rule going forward".
import { NotFound } from "./pages/NotFound.jsx";
import Today from "./pages/Today.jsx";
import Walk from "./pages/Walk.jsx";
import Chat from "./pages/Chat.jsx";
import { RequireFeature } from "./lib/useFeatures.js";

// Routing shell. All authenticated routes share the Layout (topbar + outlet).
// The catch-all `*` route renders the SEVRIC-styled 404 inside the Layout
// so the topbar still shows — better UX than a stranded raw page.
//
// ToasterProvider wraps everything inside the authed tree so any page or
// component can `useToaster()` without prop-drilling.
export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToasterProvider>
          <Routes>
            {/* TV mode — auth-gated but renders WITHOUT the layout chrome
                so the showroom display is full-bleed. */}
            <Route path="tv" element={<RequireAuth><RequireFeature name="tvMode"><TvMode /></RequireFeature></RequireAuth>} />
            {/* Weekly report — auth-gated, NO layout chrome, white-on-black
                print-optimized. Opens in a new tab from the Manager page. */}
            <Route path="report" element={<RequireAuth><RequireFeature name="report"><Report /></RequireFeature></RequireAuth>} />
            <Route element={<RequireAuth><Layout /></RequireAuth>}>
              {/* Root = Today. The legacy Home.jsx page is no longer
                  routed. Bookmarks to "/" still resolve. */}
              <Route index             element={<Navigate to="/today" replace />} />
              <Route path="today"      element={<RequireFeature name="today"><Today /></RequireFeature>} />
              <Route path="walk"       element={<RequireFeature name="walk"><Walk /></RequireFeature>} />
              {/* SEVRIC Chat — shared messaging surface across every SEVRIC app */}
              <Route path="chat"       element={<RequireFeature name="chat"><Chat /></RequireFeature>} />
              <Route path="inventory"  element={<RequireFeature name="inventory"><Inventory /></RequireFeature>} />
              <Route path="check/:id"  element={<RequireFeature name="walk"><CheckIn /></RequireFeature>} />
              {/* Alias for widget links — keeps both URL shapes working. */}
              <Route path="check-in/:id" element={<RequireFeature name="walk"><CheckIn /></RequireFeature>} />
              <Route path="manager"    element={<RequireFeature name="manager"><Manager /></RequireFeature>} />
              <Route path="units/new"  element={<RequireFeature name="addUnit"><AddUnit /></RequireFeature>} />
              {/* Alias used by Find / starter widgets. */}
              <Route path="add-unit"   element={<RequireFeature name="addUnit"><AddUnit /></RequireFeature>} />
              <Route path="import"     element={<RequireFeature name="importDar"><ImportDar /></RequireFeature>} />
              <Route path="*"          element={<NotFound />} />
            </Route>
          </Routes>
        </ToasterProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
